import type { ReactNode } from 'react';
import { useAuth } from './auth-context';
import type { Permission, UserRole } from '../types';

interface PermissionGuardProps {
  children: ReactNode;
  /**
   * Single permission required
   */
  permission?: Permission;
  /**
   * All permissions required (AND logic)
   */
  permissions?: Permission[];
  /**
   * Any of these permissions required (OR logic)
   */
  anyPermission?: Permission[];
  /**
   * Required role
   */
  role?: UserRole;
  /**
   * Content to show when access is denied
   */
  fallback?: ReactNode;
}

/**
 * Guard component that conditionally renders children based on permissions.
 *
 * @example
 * <PermissionGuard permission="courses:edit">
 *   <EditCourseButton />
 * </PermissionGuard>
 *
 * @example
 * <PermissionGuard anyPermission={['courses:edit', 'admin:access']}>
 *   <AdminPanel />
 * </PermissionGuard>
 */
export function PermissionGuard({
  children,
  permission,
  permissions,
  anyPermission,
  role,
  fallback = null,
}: PermissionGuardProps) {
  const { hasPermission, hasAllPermissions, hasAnyPermission, hasRole } = useAuth();

  // Check single permission
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // Check all permissions (AND)
  if (permissions && !hasAllPermissions(permissions)) {
    return <>{fallback}</>;
  }

  // Check any permission (OR)
  if (anyPermission && !hasAnyPermission(anyPermission)) {
    return <>{fallback}</>;
  }

  // Check role
  if (role && !hasRole(role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
